import queryString from 'querystring';
import { isArray } from 'lodash';
import {
  PaginatedSearchParams,
  PoFilters,
} from '../../Lib/Hooks/purchase-orders.hooks';
import { StatusFilterItem } from './types';
import {
  defaultSearchParams,
  startingFilters,
  vendorRevisionRequestedStatus,
} from './constant';
import { PurchaseOrderRevisionStatus } from '../../Lib/Types/purchase-order-revision';

/**
 * Parse the search params from the url and return a PaginatedSearchParams object
 * @param searchString - The search params from the url
 * @returns The parsed search params
 */
export const parsePaginatedSearchParams = (
  searchString: string,
  isCancelledEnabled: boolean,
  isPoRevisionEnabled: boolean,
): PaginatedSearchParams => {
  const decodedParams = queryString.parse(searchString.slice(1));
  // Create a deep copy of the default parameters
  const parsedParams: PaginatedSearchParams = JSON.parse(
    JSON.stringify(defaultSearchParams),
  );

  if (decodedParams.page && !isArray(decodedParams.page)) {
    parsedParams.page = parseInt(decodedParams.page, 10);
  }

  if (decodedParams.size && !isArray(decodedParams.size)) {
    parsedParams.size = parseInt(decodedParams.size, 10);
  }

  if (decodedParams.search && !isArray(decodedParams.search)) {
    parsedParams.search = decodedParams.search;
  }

  if (decodedParams.sort && !isArray(decodedParams.sort)) {
    parsedParams.sort = decodedParams.sort;
  }

  if (
    decodedParams.dueDateFrom &&
    !isArray(decodedParams.dueDateFrom) &&
    decodedParams.dueDateTo &&
    !isArray(decodedParams.dueDateTo)
  ) {
    parsedParams.filter.due_date = {
      from: decodedParams.dueDateFrom,
      to: decodedParams.dueDateTo,
    };
  }

  if (decodedParams.subStages && !isArray(decodedParams.subStages)) {
    parsedParams.filter.sub_stages = decodedParams.subStages.split(',');
  }

  if (
    decodedParams.isCancelled === 'true' ||
    decodedParams.isCancelled === 'false'
  ) {
    parsedParams.filter.is_cancelled = decodedParams.isCancelled === 'true';
  }

  if (isCancelledEnabled === false) {
    delete parsedParams.filter.is_cancelled;
  }

  if (
    decodedParams.vendorRevisionStatus &&
    !isArray(decodedParams.vendorRevisionStatus)
  ) {
    parsedParams.filter.vendor_revision_status =
      decodedParams.vendorRevisionStatus.split(',');
  }

  if (isPoRevisionEnabled === false) {
    delete parsedParams.filter.vendor_revision_status;
  }

  return parsedParams;
};

/**
 * Parse the PaginatedSearchParams object and return a query string
 * @param params - The PaginatedSearchParams object
 * @returns The query string
 */
export const parsePaginatedSearchParamsToQueryString = (
  params: PaginatedSearchParams,
) => {
  const flattenedParams: Record<string, string | number | boolean> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (key !== 'filter') {
      flattenedParams[key] = value as string | number | boolean;
    }
  });

  if (params.filter) {
    if (params.filter.due_date) {
      const { from, to } = params.filter.due_date;
      flattenedParams.dueDateFrom = from;
      flattenedParams.dueDateTo = to;
    }

    if (params.filter.sub_stages) {
      flattenedParams.subStages = params.filter.sub_stages.join(',');
    }

    if (params.filter.is_cancelled !== undefined) {
      flattenedParams.isCancelled = params.filter.is_cancelled;
    }
    if (params.filter.vendor_revision_status !== undefined) {
      flattenedParams.vendorRevisionStatus =
        params.filter.vendor_revision_status.join(',');
    }
  }

  return flattenedParams;
};

/**
 * Aggregate the filters from the StatusFilterItem array and return a flattened PoFilters object
 * @param filters - The StatusFilterItem array
 * @returns The aggregated filters
 */
export const aggregateFilters = (filters: StatusFilterItem[]): PoFilters => {
  const aggregatedFilters: PoFilters = {};

  filters.forEach((filter) => {
    Object.entries(filter.filters).forEach(([key, value]) => {
      if (key === 'sub_stages') {
        aggregatedFilters.sub_stages = [
          ...(aggregatedFilters.sub_stages || []),
          ...(value as string[]),
        ];
      }
      if (key === 'is_cancelled') {
        aggregatedFilters.is_cancelled = value as boolean;
      }
      if (key === 'vendor_revision_status') {
        aggregatedFilters.vendor_revision_status = [
          ...(aggregatedFilters.vendor_revision_status || []),
          ...(value as string[]),
        ];
      }
    });
  });

  return aggregatedFilters;
};

/**
 * Get the filters items from the aggregated filters
 * @param aggregatedFilters - The aggregated filters
 * @returns The filters items
 */
export const getFiltersItemsFromAggregatedFilters = (
  aggregatedFilters: PoFilters,
  cancelledPoEnabled: boolean,
  poRevisionEnabled: boolean,
): StatusFilterItem[] => {
  const filtersItems: StatusFilterItem[] = [];

  if (aggregatedFilters.sub_stages) {
    aggregatedFilters.sub_stages.forEach((subStage) => {
      startingFilters.forEach((filterGroup) => {
        filterGroup.items.forEach((filterItem) => {
          if (filterItem.filters.sub_stages?.includes(subStage)) {
            filtersItems.push(filterItem);
          }
        });
      });
    });
  }

  if (aggregatedFilters.is_cancelled && cancelledPoEnabled) {
    filtersItems.push({
      label: 'Cancelled',
      key: 'cancelled',
      filters: {
        is_cancelled: true,
      },
    });
  }
  if (aggregatedFilters.vendor_revision_status && poRevisionEnabled) {
    const requestedStatuses = aggregatedFilters.vendor_revision_status.filter(
      (status) =>
        vendorRevisionRequestedStatus.includes(
          status as PurchaseOrderRevisionStatus,
        ),
    );
    if (requestedStatuses.length > 0) {
      filtersItems.push({
        label: 'Revision Requested',
        key: 'revision-requested',
        filters: {
          vendor_revision_status: requestedStatuses,
        },
      });
    }
  }
  return filtersItems;
};
